import type { Ref } from 'vue';
import { autoUpdate, getOverflowAncestors } from '@floating-ui/dom';
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { AnimateHelper } from '@/shared/legacy/lib/util/animate-helper';

/** How far the card's bottom edge must pass the top of the scroller before the pill shows up. */
const TRIGGER_DISTANCE = 100;
/** Gap to the scroller edges. Lives here, not in CSS: the horizontal padding derives from it. */
const EDGE_INSET = 8;

const FADE_DURATION = 150;
const FADE_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
/** How far above its resting place the pill drifts in from. */
const SLIDE_DISTANCE = 8;
/** Spelled out per property: `autoUpdate` rewrites the box on resize, which must not animate. */
const REVEAL_TRANSITION = ['opacity', 'transform']
  .map((property) => `${property} ${FADE_DURATION}ms ${FADE_EASING}`)
  .join(', ');

const SCROLL_BACK_DURATION = 300;

const SCROLLABLE = new Set(['auto', 'scroll', 'overlay']);

/** `idle` is in the flow; the rest are out of it, `entering` the frame that seeds the reveal. */
type Phase = 'idle' | 'entering' | 'visible' | 'leaving';

type Box = {
  top: number;
  left: number;
  width: number;
  paddingLeft: number;
  paddingRight: number;
};

/** `getOverflowAncestors` also yields `overflow: hidden`, which does not scroll. */
const findScroller = (el: HTMLElement) => {
  const scrollable = getOverflowAncestors(el).find((node) => {
    return node instanceof HTMLElement && SCROLLABLE.has(getComputedStyle(node).overflowY);
  });
  return (scrollable as HTMLElement | undefined) ?? null;
};

/** Two frames — the parked state must be committed or the transition has nothing to run from. */
const afterNextPaint = (fn: () => void) => {
  let inner = 0;
  const outer = requestAnimationFrame(() => {
    inner = requestAnimationFrame(fn);
  });
  return () => {
    cancelAnimationFrame(outer);
    cancelAnimationFrame(inner);
  };
};

/**
 * Pins a card's info block to the top of the scroll container once the card has gone
 * `TRIGGER_DISTANCE` past it. The card only triggers the effect — the pill is bounded by nothing
 * and floats over whatever scrolls underneath, so the card passed most recently paints on top.
 *
 * `position: fixed` does the pinning, so scrolling writes nothing unless the phase changes. A
 * placeholder holds the block's space, measured before the block leaves the flow.
 */
export function useFloatingInfo(enabled: Ref<boolean>) {
  const rootEl = ref<HTMLElement | null>(null);
  const placeholderEl = ref<HTMLElement | null>(null);
  const infoEl = ref<HTMLElement | null>(null);

  const phase = ref<Phase>('idle');
  const box = shallowRef<Box>();
  const height = ref(0);

  let scroller: HTMLElement | null = null;
  let frame = 0;
  let cardObserver: ResizeObserver | null = null;
  let stopAutoUpdate: (() => void) | null = null;
  /** Undoes whatever the current phase scheduled, so reversals don't fight. */
  let cancelPending: (() => void) | null = null;
  /** The double-click jump owns the pill until it lands. */
  let isScrollingBack = false;
  let scrollBackTimer = 0;

  const isFloating = computed(() => phase.value !== 'idle');
  /** Where the geometry wants it, as opposed to where it is mid-transition. */
  const isWanted = () => phase.value !== 'idle' && phase.value !== 'leaving';

  /** The edge the pill sits on. Client box, not bounding rect: excludes the scrollbar gutter. */
  const viewportTop = () => {
    return scroller ? scroller.getBoundingClientRect().top + scroller.clientTop : 0;
  };

  const infoStyle = computed(() => {
    if (!box.value) {
      return undefined;
    }
    return {
      top: `${box.value.top}px`,
      left: `${box.value.left}px`,
      width: `${box.value.width}px`,
      paddingLeft: `${box.value.paddingLeft}px`,
      paddingRight: `${box.value.paddingRight}px`,
      opacity: phase.value === 'visible' ? 1 : 0,
      transform: phase.value === 'visible' ? 'translateY(0)' : `translateY(-${SLIDE_DISTANCE}px)`,
      transition: phase.value === 'entering' ? 'none' : REVEAL_TRANSITION
    };
  });

  const placeholderStyle = computed(() => {
    return isFloating.value ? { height: `${height.value}px` } : undefined;
  });

  const update = () => {
    const placeholder = placeholderEl.value;
    const root = rootEl.value;
    if (!placeholder || !root || !isFloating.value) {
      return;
    }
    const inFlow = placeholder.getBoundingClientRect();
    const card = root.getBoundingClientRect();

    // Sized off the card, not off the scroller: the layout caps the card's column well short of
    // the scroller's width, and spanning the scroller would drag the block's right-hand content
    // away from where it sits in the flow. Inset by EDGE_INSET and padded by the gaps the block
    // has to the card's edges less that inset, both edges land back on the in-flow box — the 4px
    // asymmetry from the stripe in the card's padding-left included.
    box.value = {
      top: viewportTop() + EDGE_INSET,
      left: card.left + EDGE_INSET,
      width: card.width - EDGE_INSET * 2,
      paddingLeft: inFlow.left - card.left - EDGE_INSET,
      paddingRight: card.right - inFlow.right - EDGE_INSET
    };
  };

  /** Back into the flow, everything the phase held released. */
  const settle = () => {
    cancelPending?.();
    cancelPending = null;
    phase.value = 'idle';
    box.value = undefined;
    stopAutoUpdate?.();
    stopAutoUpdate = null;
  };

  const setFloating = (value: boolean) => {
    if (value === isWanted()) {
      return;
    }
    cancelPending?.();
    cancelPending = null;

    if (!value) {
      phase.value = 'leaving';
      const timer = window.setTimeout(settle, FADE_DURATION);
      cancelPending = () => window.clearTimeout(timer);
      return;
    }

    // caught mid fade-out: still out of the flow and positioned, so just fade back up
    if (phase.value === 'leaving') {
      phase.value = 'visible';
      return;
    }

    const placeholder = placeholderEl.value;
    const info = infoEl.value;
    if (!placeholder || !info) {
      return;
    }
    // Off the placeholder, while still in the flow: by definition the space to hold, and it
    // cannot pick up the padding the block wears once pinned.
    height.value = placeholder.getBoundingClientRect().height;
    phase.value = 'entering';
    update();
    stopAutoUpdate = autoUpdate(placeholder, info, update, { ancestorScroll: false });
    cancelPending = afterNextPaint(() => {
      cancelPending = null;
      phase.value = 'visible';
    });
  };

  /**
   * Not an `IntersectionObserver`: it reports only on a change of intersection state, and both
   * sides of the trigger line are "not intersecting" — a jump straight across (a programmatic
   * `scrollTo`, a restored position, log items shifting the card) yields no callback at all.
   */
  const evaluate = () => {
    frame = 0;
    const root = rootEl.value;
    const placeholder = placeholderEl.value;
    if (isScrollingBack || !root || !placeholder) {
      return;
    }
    // `display: none` (eg. the card still hidden behind a `v-show` while its content loads)
    // collapses the rect to zero, which would otherwise read as "already scrolled past".
    if (root.getClientRects().length === 0) {
      return;
    }
    const top = viewportTop();
    setFloating(root.getBoundingClientRect().bottom <= top - TRIGGER_DISTANCE);

    // A fade-out still running once the block's own slot is back in view would sit on top of the
    // real thing and then blink out, so cut it short the moment that slot appears.
    if (phase.value === 'leaving' && placeholder.getBoundingClientRect().bottom > top) {
      settle();
    }
  };

  const schedule = () => {
    if (!frame) {
      frame = requestAnimationFrame(evaluate);
    }
  };

  /**
   * `AnimateHelper.scrollIntoView` targets `scrollTop + rect.top`, which overshoots for a scroller
   * that doesn't start at the top of the window. `scrollTo` also cancels an animation already
   * running on the element, so repeated double clicks don't fight.
   */
  const scrollToCard = (event: MouseEvent) => {
    const root = rootEl.value;
    if (!isWanted() || !root) {
      return;
    }
    // empty space only: anything inside the pill is someone else's click
    if (event.target !== event.currentTarget) {
      return;
    }
    const el = scroller ?? document.documentElement;
    const top = el.scrollTop + root.getBoundingClientRect().top - viewportTop() - EDGE_INSET;

    // The pill goes at once and the trigger is held off until the card lands: fading it out over
    // the card flying back would only read as a blink. Held on a timer rather than the tween's
    // `end` callback, which is skipped when something else interrupts the tween.
    settle();
    isScrollingBack = true;
    window.clearTimeout(scrollBackTimer);
    scrollBackTimer = window.setTimeout(() => {
      isScrollingBack = false;
    }, SCROLL_BACK_DURATION);

    AnimateHelper.scrollTo(
      el,
      // `left` defaults to 0, not to the current offset
      {
        left: el.scrollLeft,
        top
      },
      {
        duration: SCROLL_BACK_DURATION,
        easing: AnimateHelper.easeOutCubic
      }
    );
  };

  const start = () => {
    const root = rootEl.value;
    if (!root) {
      return;
    }
    scroller = findScroller(root);
    scroller?.addEventListener('scroll', schedule, { passive: true });
    // the scroller itself can be moved by the page around it
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    // the card grows as comments and log items load, moving its bottom edge
    cardObserver = new ResizeObserver(schedule);
    cardObserver.observe(root);
    evaluate();
  };

  const stop = () => {
    settle();
    window.clearTimeout(scrollBackTimer);
    isScrollingBack = false;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
    scroller?.removeEventListener('scroll', schedule);
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    cardObserver?.disconnect();
    cardObserver = null;
  };

  watch([enabled, rootEl, infoEl], ([isEnabled, root, info]) => {
    stop();
    if (isEnabled && root && info) {
      start();
    }
  });

  onBeforeUnmount(stop);

  return {
    rootEl,
    placeholderEl,
    infoEl,
    isFloating,
    infoStyle,
    placeholderStyle,
    scrollToCard
  };
}
